import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {IterableUtil} from '@shared/util/collections/collection-utils';
import {AceLogger} from '@logging/ace-logger';
import {AceLoggerService} from '@logging/ace-logger.service';


enum StorageType {
  LOCAL_STORAGE = 'localStorage',
  SESSION_STORAGE = 'sessionStorage'
}

interface StoredEntry<T> {
  value: T;
  expiresAt?: number;
}

@Injectable({providedIn: 'root'})
export class StorageService {
  readonly local: ExpiringStore;
  readonly session: ExpiringStore;

  constructor(@Inject(DOCUMENT) document: Document, logService: AceLoggerService) {
    const logger = logService.create(StorageService.name);
    this.local = new ExpiringStore(document, StorageType.LOCAL_STORAGE, logger);
    this.session = new ExpiringStore(document, StorageType.SESSION_STORAGE, logger);
  }

}


/**
 * One web storage area's entries, each with an optional lifetime. Where that area is unavailable the entries go to
 * cookies instead, which are shared across tabs - so a session store loses its per-tab isolation on that fallback.
 */
export class ExpiringStore {

  private readonly storage: Storage | null;

  constructor(private document: Document, type: StorageType, private logger: AceLogger) {
    this.storage = this.isStorageSupported(type) ? document.defaultView[type] : null;
    this.removeExpiredEntries();
  }

  get<T>(key: string): T | null {
    return this.readValue(key);
  }

  /** Set throwOnFailure to false where having nowhere to store the value is survivable, and a logged error will do. */
  set(key: string, value: unknown, ttl?: number, throwOnFailure = true): void {
    const entry: StoredEntry<unknown> = {value, ...ttl != null && {expiresAt: Date.now() + ttl}};
    const storedValue = JSON.stringify(entry);
    try {
      if (this.storage) {
        this.storage.setItem(key, storedValue);
      } else {
        this.writeCookie(key, storedValue, ttl);
      }
    } catch (error) {
      const message = 'Unable to store [' + key + ']';
      if (throwOnFailure) {
        throw new Error(message, {cause: error});
      } else {
        this.logger.error(message, error);
      }
    }
  }

  pop<T>(key: string): T | null {
    const value = this.readValue<T>(key);
    this.remove(key);
    return value;
  }

  // Deliberately stricter than https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#feature-detecting_localstorage,
  // which reports a storage at quota as supported because the API still reads. This store exists to write, so one that refuses writes is no use to it.
  private isStorageSupported(type: StorageType): boolean {
    try {
      const storage: Storage = this.document.defaultView[type];
      const testKey = '__storageTest__';
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private readValue<T>(key: string): T | null {
    const entry = this.parseEntry<T>(this.storage ? this.storage.getItem(key) : this.readCookie(key));
    if (entry == null) {
      return null;
    } else if (entry.expiresAt == null || entry.expiresAt > Date.now()) {
      return entry.value;
    } else {
      this.remove(key);
      return null;
    }
  }

  /** Null for anything that is not one of our envelopes, so that a sweep only ever deletes entries this store wrote. */
  private parseEntry<T>(storedValue: string | null): StoredEntry<T> | null {
    try {
      const {value, expiresAt} = JSON.parse(storedValue) ?? {};
      const isEntry = value != null && (expiresAt == null || typeof expiresAt === 'number');
      return isEntry ? {value, expiresAt} : null;
    } catch {
      return null;
    }
  }

  private removeExpiredEntries(): void {
    try {
      if (this.storage) {
        const now = Date.now();
        const expiredKeys = Object.keys(this.storage).filter(key => this.parseEntry(this.storage.getItem(key))?.expiresAt <= now);
        expiredKeys.forEach(key => this.storage.removeItem(key));
      }
    } catch (error) {
      this.logger.error('Unable to clear expired entries', error);
    }
  }

  private remove(key: string): void {
    if (this.storage) {
      this.storage.removeItem(key);
    } else {
      this.deleteCookie(key);
    }
  }

  /** A cookie holds at most ~4KB, so a value beyond that is silently dropped by the browser and reads back as null. */
  private writeCookie(key: string, value: string, ttl?: number): void {
    const maxAge = ttl == null ? '' : '; max-age=' + Math.floor(ttl / 1000);
    const secure = this.document.location.protocol === 'https:' ? '; secure' : '';
    // samesite is hardening only - the value is read back through document.cookie on our own origin, which no cookie attribute gates.
    this.document.cookie = encodeURIComponent(key) + '=' + encodeURIComponent(value) + maxAge + '; path=/; samesite=lax' + secure;
  }

  private deleteCookie(key: string): void {
    try {
      this.writeCookie(key, '', 0);
    } catch (error) {
      this.logger.error('Unable to delete cookie [' + key + ']', error);
    }
  }

  private readCookie(key: string): string | null {
    try {
      const encodedKey = encodeURIComponent(key);
      const cookie = IterableUtil.first(IterableUtil.filter(
        IterableUtil.map(this.document.cookie.split(';'), entry => entry.trim()),
        entry => entry.startsWith(encodedKey + '=')
      ));
      return cookie ? decodeURIComponent(cookie.substring(encodedKey.length + 1)) : null;
    } catch {
      return null;
    }
  }

}
